import type { AIInterviewPlanQuestion } from '@ispot/shared'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ChevronUp, ChevronDown, Plus, Trash2 } from 'lucide-react'

interface QuestionCardProps {
  question: AIInterviewPlanQuestion
  index: number
  total: number
  onUpdate: (patch: Partial<AIInterviewPlanQuestion>) => void
  onMove: (dir: -1 | 1) => void
  onRemove: () => void
}

export function QuestionCard({ question, index, total, onUpdate, onMove, onRemove }: QuestionCardProps) {
  const toggleType = () => {
    onUpdate(
      question.type === 'open'
        ? { type: 'mcq', mcqOptions: ['Option 1', 'Option 2'] }
        : { type: 'open', mcqOptions: undefined },
    )
  }

  return (
    <div className="rounded-md border border-border bg-bg-card p-3">
      <div className="flex items-start gap-2">
        <span className="mt-1.5 font-mono text-caption text-fg-muted">{index + 1}</span>
        <Textarea
          value={question.text}
          onChange={(e) => onUpdate({ text: e.target.value })}
          placeholder="Question text..."
          rows={2}
          className="flex-1 text-sm"
        />
        <div className="flex flex-col gap-0.5">
          <button
            onClick={() => onMove(-1)}
            disabled={index === 0}
            className="text-fg-muted hover:text-fg disabled:opacity-30"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            className="text-fg-muted hover:text-fg disabled:opacity-30"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2">
        <Badge variant="outline" className="cursor-pointer select-none border-border text-fg-secondary hover:border-accent-500/50 hover:text-fg" onClick={toggleType}>
          {question.type}
        </Badge>
        <button onClick={onRemove} className="ml-auto text-fg-muted hover:text-danger-fg">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {question.type === 'mcq' && question.mcqOptions && (
        <div className="mt-2 space-y-1.5">
          {question.mcqOptions.map((opt, oi) => (
            <div key={oi} className="flex items-center gap-1.5">
              <span className="w-4 text-caption text-fg-muted">{String.fromCharCode(65 + oi)}.</span>
              <Input
                value={opt}
                onChange={(e) => {
                  const opts = [...(question.mcqOptions ?? [])]
                  opts[oi] = e.target.value
                  onUpdate({ mcqOptions: opts })
                }}
                className="h-7 flex-1 text-body"
              />
              <button
                onClick={() => onUpdate({ mcqOptions: question.mcqOptions?.filter((_, i) => i !== oi) })}
                className="text-fg-muted hover:text-danger-fg"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button
            onClick={() => onUpdate({ mcqOptions: [...(question.mcqOptions ?? []), ''] })}
            className="mt-1 flex items-center gap-1 text-caption text-accent-600 hover:text-accent-700"
          >
            <Plus className="w-3 h-3" /> Add option
          </button>
        </div>
      )}
    </div>
  )
}
